import React, { useCallback } from "react";
import { Chip, Link, Typography } from "@mui/material";
import { getAlmacenColor, getBadgeStyles } from "../../../components/ui/badgeColors";
import { DATA_GRID_FONT_SIZE } from "../../../components/ui/dataGridStyles";
import Utils from "../../../utils/Utils";
import AccionesEstadoVenta from "../components/ui/AccionesEstadoVenta";
import EstadoVentaBadge from "../components/ui/EstadoVentaBadge";
import { VALOR_VACIO, formatFecha, formatTexto } from "../utils/formatters";
import { getEnlaceInfoEstadoVenta, normalizarCodigoAlmacen } from "./estadosVentas";

const SX_ENLACE = { fontSize: DATA_GRID_FONT_SIZE.CELDA, fontWeight: 600, color: "#cc071e" };
const SX_TEXTO = { fontSize: DATA_GRID_FONT_SIZE.CELDA };
const NAVE_ESTILO = { height: 22, fontSize: 11 };

const detenerPropagacion = (e) => e.stopPropagation();

const textoOGuion = ({ value }) => (
    <Typography component="span" sx={SX_TEXTO}>{formatTexto(value)}</Typography>
);

const celdaNumero = ({ row }) => (
    <Link
        href={getEnlaceInfoEstadoVenta(row)}
        underline="hover"
        rel="noopener noreferrer"
        onClick={detenerPropagacion}
        sx={SX_ENLACE}
    >
        {row.numero}
    </Link>
);

// DECISION: chip con el color del almacén (regla de almacenes diferenciados de CLAUDE.md);
// sin nombre de nave no hay chip, solo "-"
const celdaNave = ({ row, value }) =>
    value ? (
        <Chip
            label={value}
            size="small"
            sx={getBadgeStyles(getAlmacenColor(normalizarCodigoAlmacen(row.almacen)), NAVE_ESTILO)}
        />
    ) : (
        textoOGuion({ value })
    );

/**
 * Celda de acciones: memoizada y con onCambiar estable por fila, para que el React.memo de
 * AccionesEstadoVenta evite repintar los botones de las filas que no cambian.
 */
const CeldaAcciones = React.memo(({ row, onCambiarEstado, disabled }) => {
    const onCambiar = useCallback((estado) => onCambiarEstado(row, estado), [onCambiarEstado, row]);
    return <AccionesEstadoVenta onCambiar={onCambiar} disabled={disabled} estadoActual={row.estado} />;
});

/**
 * Columnas fijas del DataGrid de estados de ventas (constante de módulo; orden y flex heredados
 * de la vista anterior de Control pedidos venta)
 */
export const ESTADOS_VENTAS_COLUMNS_BASE = [
    { field: "numero", headerName: "Número", flex: 0.8, renderCell: celdaNumero },
    {
        field: "tipo",
        headerName: "Tipo",
        flex: 1,
        valueFormatter: (params) => Utils.toCapitalize(params?.value) || VALOR_VACIO,
    },
    { field: "preparado", headerName: "Preparando", flex: 1, renderCell: textoOGuion },
    { field: "revisado", headerName: "Revisando", flex: 1, renderCell: textoOGuion },
    { field: "camara", headerName: "Cámara", flex: 0.8, renderCell: textoOGuion },
    { field: "ruta", headerName: "Ruta", flex: 1.2, filterable: false, renderCell: textoOGuion },
    { field: "cliente", headerName: "Cliente", flex: 2.3, filterable: false, renderCell: textoOGuion },
    {
        field: "fecha",
        headerName: "Fecha albarán",
        flex: 1,
        filterable: false,
        valueFormatter: (params) => formatFecha(params?.value),
    },
    {
        field: "estado",
        headerName: "Estado",
        flex: 1,
        filterable: false,
        renderCell: (params) => <EstadoVentaBadge estado={params.value} />,
    },
    { field: "nave", headerName: "Nave", flex: 0.6, filterable: false, renderCell: celdaNave },
];

/**
 * Devuelve las 11 columnas; solo la de acciones depende de los callbacks.
 *
 * Uso en la página (TASK-071): memoizar con las dependencias y pasar un onCambiarEstado
 * estable (useCallback), de modo que las columnas solo se regeneren cuando cambia la fila
 * en vuelo:
 *   const columns = useMemo(
 *       () => generateEstadosVentasColumns({ onCambiarEstado, cambiandoId }),
 *       [onCambiarEstado, cambiandoId]);
 * onCambiarEstado(fila, estado) recibe la fila completa y el ORDER_STATE elegido.
 */
export const generateEstadosVentasColumns = ({ onCambiarEstado, cambiandoId } = {}) => [
    ...ESTADOS_VENTAS_COLUMNS_BASE,
    {
        field: "actions",
        type: "actions",
        headerName: "",
        width: 150,
        sortable: false,
        filterable: false,
        cellClassName: "actions",
        renderCell: ({ row }) => (
            <CeldaAcciones
                row={row}
                onCambiarEstado={onCambiarEstado}
                disabled={cambiandoId === row.id}
            />
        ),
    },
];
